import * as React from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { createPortal } from "react-dom";
import { useMe } from "./useMe.js";
import { apiFetch, ApiError } from "../lib/api.js";
import { track } from "../lib/analytics.js";
import { isValidEmail } from "../lib/validation.js";
import { SupportHint } from "../components/MailtoContact.js";

/**
 * Shared "start Stripe Checkout" controller, used by every Subscribe button
 * (the paywall modal and the /plans CTAs). It collapses the old
 * paywall → /plans → /subscription funnel: both surfaces now go straight to
 * Stripe via this hook.
 *
 * `subscribe()` branches on session state:
 *   - signed out  → /signin (the old /subscription redirect)
 *   - premium     → /dashboard (where the manage card now lives)
 *   - free        → POST /v1/billing/checkout, then redirect to Stripe
 *
 * After Checkout, Stripe returns to the celebration at `/welcome?success=1`
 * (+ `&next=` when the caller passed one — e.g. the paywall hands back the
 * game it gated, so /welcome can return the buyer to it once Premium activates).
 *
 * Phone-only accounts with no email get an `email_required` (400) from the
 * API; the hook surfaces a small inline capture modal (`emailModal`) and
 * retries with the address — this replaces the inline email field that lived
 * on the deleted /subscription page.
 */
export interface UseSubscribeOptions {
  /** Destination after Premium activates; rides the success URL as `?next=`. */
  next?: string | null;
}

export interface UseSubscribeResult {
  /** Start checkout. Pass an optional promo code (validated server-side). */
  subscribe: (coupon?: string) => void;
  busy: boolean;
  error: string | null;
  /** Render this in the consumer's tree. It's a portal that only paints when a
   *  no-email account needs to add one mid-checkout; otherwise `null`. */
  emailModal: React.ReactElement | null;
}

export function useSubscribe(options: UseSubscribeOptions = {}): UseSubscribeResult {
  const { next } = options;
  const { data: me } = useMe();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  // The typed email collides with another account. Rendered as its own message
  // (with an inline "sign in" link) rather than a plain string, so it's tracked
  // separately from `error`.
  const [emailInUse, setEmailInUse] = React.useState(false);
  const [needEmail, setNeedEmail] = React.useState(false);
  const [email, setEmail] = React.useState("");
  // Remember the promo code from the initiating click so the email-capture
  // retry re-submits it (the API still validates + applies it, including the
  // 100%-off-forever path that skips card collection).
  const couponRef = React.useRef<string>("");

  const runCheckout = React.useCallback(
    async (emailArg?: string) => {
      setBusy(true);
      setError(null);
      setEmailInUse(false);
      try {
        const origin = window.location.origin;
        const successUrl =
          `${origin}/welcome?success=1` + (next ? `&next=${encodeURIComponent(next)}` : "");
        const { checkout_url } = await apiFetch<{ checkout_url: string }>("/v1/billing/checkout", {
          method: "POST",
          body: {
            success_url: successUrl,
            cancel_url: `${origin}/plans`,
            email: emailArg?.trim() || undefined,
            coupon: couponRef.current.trim() || undefined,
          },
        });
        if (checkout_url) window.location.href = checkout_url;
      } catch (e) {
        // ApiError.message carries the API's error *code*, not a sentence.
        if (e instanceof ApiError && e.message === "email_required") {
          setNeedEmail(true);
        } else if (e instanceof ApiError && e.message === "email_in_use") {
          // The address belongs to a different account. The modal renders a
          // dedicated message with an inline "sign in" link (see `onSignIn`).
          setEmailInUse(true);
        } else if (e instanceof ApiError && e.message === "invalid_coupon") {
          setError("That coupon code isn't valid. Check it and try again, or leave it blank.");
        } else if (e instanceof ApiError && e.message === "already_subscribed") {
          // Stale local state — they already have Premium. Send them to manage.
          navigate("/dashboard");
          return;
        } else {
          setError("Something went wrong. Please try again.");
        }
      } finally {
        setBusy(false);
      }
    },
    [next, navigate],
  );

  const subscribe = React.useCallback(
    (coupon?: string) => {
      if (!me?.user) {
        navigate("/signin");
        return;
      }
      if (me.premium) {
        navigate("/dashboard");
        return;
      }
      couponRef.current = coupon ?? "";
      track.subscribeCheckoutStarted({ has_coupon: couponRef.current.trim().length > 0 });
      void runCheckout();
    },
    [me, navigate, runCheckout],
  );

  // The "sign in" link in the email-in-use message. This modal only ever shows
  // to a signed-in, email-less account, so we must drop that phone session
  // first — otherwise SigninRoute bounces an already-signed-in visitor straight
  // back home. Then hand off to /signin with the address pre-filled, ready to
  // sign in to the account that already owns it.
  const goManageExistingAccount = React.useCallback(async () => {
    // `busy` both gates the link (no duplicate logout on a double-click) and
    // reuses the modal's existing in-flight affordance. We navigate away on
    // every path, so there's no need to reset it.
    setBusy(true);
    try {
      await apiFetch("/auth/logout", { method: "POST" });
    } catch {
      // Even if logout fails, clearing identity + routing to /signin is the
      // right move; the magic-link verify there replaces any lingering session.
    }
    // Cancel any in-flight ['me'] refetch first: otherwise a fetch that started
    // before logout could resolve afterward and re-populate the cache with the
    // now-stale signed-in user, making SigninRoute bounce the visitor home.
    // Then write a fresh null — useMe's 60s staleTime keeps it from refetching
    // immediately on the signin page.
    await queryClient.cancelQueries({ queryKey: ["me"] });
    queryClient.setQueryData(["me"], null);
    setNeedEmail(false);
    navigate(`/signin?method=email&email=${encodeURIComponent(email.trim())}`);
  }, [email, queryClient, navigate]);

  const emailModal = needEmail
    ? createPortal(
        <SubscribeEmailModal
          email={email}
          busy={busy}
          error={error}
          emailInUse={emailInUse}
          onSignIn={() => void goManageExistingAccount()}
          onEmailChange={(value) => {
            setEmail(value);
            // Editing the address invalidates the previous attempt's result.
            // Drop the email-in-use message + its "sign in" link so it can't
            // sign the user out and redirect using a new address that was
            // never checked for a collision.
            setEmailInUse(false);
            setError(null);
          }}
          onSubmit={() => void runCheckout(email)}
          onClose={() => {
            setNeedEmail(false);
            setError(null);
            setEmailInUse(false);
          }}
        />,
        document.body,
      )
    : null;

  return { subscribe, busy, error, emailModal };
}

/**
 * Minimal, dependency-free email-capture step for the rare phone-only account
 * that must add an email before Stripe can bill it. Self-styled so it renders
 * the same over the dark paywall modal or the /plans page.
 */
function SubscribeEmailModal({
  email,
  busy,
  error,
  emailInUse,
  onSignIn,
  onEmailChange,
  onSubmit,
  onClose,
}: {
  email: string;
  busy: boolean;
  error: string | null;
  /** The typed email collides with another account — show the sign-in message. */
  emailInUse: boolean;
  /** Drop the current session and hand off to /signin (see the hook). */
  onSignIn: () => void;
  onEmailChange: (value: string) => void;
  onSubmit: () => void;
  onClose: () => void;
}): React.ReactElement {
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  React.useEffect(() => {
    const t = window.setTimeout(() => inputRef.current?.focus(), 0);
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKey);
    return () => {
      window.clearTimeout(t);
      document.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  const canSubmit = isValidEmail(email) && !busy;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Add your email to subscribe"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      style={{
        position: "fixed",
        inset: 0,
        // Max int: this opens from *inside* the paywall, so it must clear the
        // auth-gate overlay (2147483645) and its nudge (2147483646). A lower
        // value rendered the capture box behind the paywall's opaque backdrop —
        // present in the DOM but invisible, so Subscribe looked dead for any
        // email-less account (it returns email_required, which should pop this).
        zIndex: 2147483647,
        background: "rgba(15, 17, 21, 0.55)",
        display: "grid",
        placeItems: "center",
        padding: 20,
      }}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (canSubmit) onSubmit();
        }}
        style={{
          width: "100%",
          maxWidth: 380,
          // Field Day cream panel + signature orange offset shadow so it matches
          // the paywall card behind it (was a generic white box with a flat dark
          // shadow, which clashed with the dark→cream Field Day theme).
          background: "#f5f1ea",
          border: "1.5px solid #0f1115",
          borderRadius: 18,
          boxShadow: "10px 12px 0 #ff7a1a",
          padding: "26px 24px",
        }}
      >
        <h2
          style={{
            fontFamily: "'IntegralCF', sans-serif",
            fontSize: 22,
            lineHeight: 1.1,
            letterSpacing: "-0.01em",
            margin: "0 0 8px",
            color: "#0f1115",
          }}
        >
          Add your email
        </h2>
        <p style={{ fontSize: 14, lineHeight: 1.5, color: "#44474f", margin: "0 0 16px" }}>
          We'll send your receipt here and use it for your subscription.
        </p>
        <input
          ref={inputRef}
          type="email"
          inputMode="email"
          autoComplete="email"
          spellCheck={false}
          placeholder="you@example.com"
          value={email}
          onChange={(e) => onEmailChange(e.target.value)}
          required
          style={{
            width: "100%",
            boxSizing: "border-box",
            height: 48,
            padding: "0 14px",
            borderRadius: 12,
            border: "1.5px solid #0f1115",
            fontSize: 15,
            fontFamily: "inherit",
            marginBottom: 14,
          }}
        />
        {emailInUse ? (
          <>
            <p
              role="alert"
              style={{ color: "#0f1115", fontSize: 13.5, lineHeight: 1.45, margin: "0 0 12px" }}
            >
              Someone already has an account with that email.
            </p>
            <button
              type="button"
              onClick={onSignIn}
              disabled={busy}
              /* Secondary / ghost pill: transparent fill + ink border, sitting
                 above the orange Continue. Pulls the "sign in" action out of the
                 error sentence so it reads as a clear, tappable choice instead of
                 a third accent color competing with the primary CTA. */
              style={{
                width: "100%",
                height: 52,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 6,
                borderRadius: 9999,
                border: "2px solid #0f1115",
                background: "transparent",
                color: "#0f1115",
                fontFamily: "inherit",
                fontWeight: 700,
                fontSize: 14.5,
                lineHeight: 1,
                cursor: busy ? "default" : "pointer",
                opacity: busy ? 0.6 : 1,
                marginBottom: 10,
              }}
            >
              {/* Show the typed address so it's clear which account they'll sign
                 into; truncate (min-width:0 + ellipsis) so a long email can't
                 overflow the fixed-width pill, keeping the arrow always visible. */}
              <span
                style={{
                  minWidth: 0,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                Sign in with {email}
              </span>
              <span aria-hidden="true">→</span>
            </button>
          </>
        ) : error ? (
          <>
            <p
              role="alert"
              style={{ color: "#c0392b", fontSize: 13.5, lineHeight: 1.45, margin: "0 0 6px" }}
            >
              {error}
            </p>
            {/* The modal portals to document.body, outside any `.v-field`, but
                `.support-hint` is a global class so it still applies. Only the
                ink color needs setting — the cream panel isn't a themed page. */}
            <SupportHint className="support-hint--inline" />
          </>
        ) : null}
        <button
          type="submit"
          disabled={!canSubmit}
          /* Field Day primary, inline: the shared `.btn-field-primary` class is
             scoped under `.v-field`, but this modal portals to document.body
             (outside any `.v-field`), so the class never applies here — replicate
             the orange pill + ink border + offset shadow directly. */
          style={{
            width: "100%",
            height: 56,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: 9999,
            border: "2px solid #0f1115",
            background: "#ff7a1a",
            color: "#fff",
            fontFamily: "inherit",
            fontWeight: 700,
            fontSize: 15,
            lineHeight: 1,
            boxShadow: "6px 6px 0 #0f1115",
            cursor: canSubmit ? "pointer" : "not-allowed",
            opacity: canSubmit ? 1 : 0.6,
          }}
        >
          {busy ? "Redirecting…" : "Continue"}
        </button>
        <button
          type="button"
          onClick={onClose}
          style={{
            width: "100%",
            marginTop: 10,
            padding: "8px 0",
            background: "none",
            border: "none",
            color: "#44474f",
            fontFamily: "inherit",
            fontSize: 13.5,
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          Cancel
        </button>
      </form>
    </div>
  );
}
